/**
 * Account Analytics (proposal 70) — a client-only derived model computed from
 * the already-synced trade history stores. No server, protocol or schema
 * changes: every number below derives from `TradeOrder` rows.
 *
 * Conventions (must match the rest of the terminal):
 * - Net P&L per trade = profit + swap - commission (same as useTradeStatistics
 *   and historySummary.netPositionPnl), balance operations skipped.
 * - Pip size = 10^-(digits-1) (same as chart-helpers useTradeHistoryLines).
 * - Drawdown is BALANCE-based (closed trades only) — label it as such in UI.
 * - SL/TP-hit detection is a labelled heuristic (placedType when available,
 *   else a tolerance match of close price vs SL/TP).
 */

import type { TradeOrder } from "./types";
import { VybeOrderType, VybePlacedType } from "./types";
import type { DateFilter } from "./stores/useTradeHistoryStore";
import { isPositionBalanceOperation, netPositionPnl } from "./historySummary";

// ─────────────────────────────────────────────────────────────── types ──

/** Scope the analytics window — mirrors the History tab's filter config. */
export interface AnalyticsScope {
  symbol: string; // "allsymbols" or a concrete symbol
  dateFilter: DateFilter; // includes "all"
  customDateFrom?: string;
  customDateTo?: string;
}

export interface EquityPoint {
  /** close time (ms) of the event that produced this balance */
  t: number;
  /** reconstructed balance (deposits + cumulative net P&L) */
  balance: number;
  /** cumulative trading net P&L (excludes balance operations) */
  profit: number;
  /** profit / deposits × 100 — null when deposits unknown/zero */
  growthPct: number | null;
}

export interface DailyBucket {
  /** local day start (ms) */
  dayStart: number;
  lots: number;
  pnl: number;
}

export interface WinLossBucket {
  /** hour 0-23 or weekday 0-6 (Sunday first, Date#getDay) */
  key: number;
  winners: number;
  losers: number;
}

export interface RiskOfRuinRow {
  lossPct: number;
  /** probability (%) of the consecutive-loss streak below; null when unknowable */
  probabilityPct: number | null;
  /** consecutive losing trades (avg size) needed to lose lossPct of balance */
  consecutiveLosses: number | null;
}

export interface DurationPoint {
  durationMs: number;
  pips: number;
  win: boolean;
}

export interface SummarySide {
  trades: number;
  pips: number;
  profit: number;
  wonPct: number;
  lostPct: number;
}

export interface SymbolSummaryGroup {
  symbol: string;
  long: SummarySide;
  short: SummarySide;
  total: SummarySide;
}

export interface MonthlyBucket {
  year: number;
  /** 0-11 */
  month: number;
  pnl: number;
  /** reconstructed balance at month open (deposits + P&L before the month) */
  openBalance: number;
  /** pnl / openBalance × 100 — null when openBalance <= 0 */
  gainPct: number | null;
  trades: number;
}

export interface SideStat {
  total: number;
  won: number;
  winRatePct: number;
}

export interface TradeStatistics {
  totalTrades: number;
  winCount: number;
  lossCount: number;
  breakEvenCount: number;
  winRatePct: number;
  totalProfit: number;
  totalLoss: number;
  netProfit: number;
  pipsTotal: number;
  avgWinPips: number;
  avgLossPips: number;
  avgWin: number;
  avgLoss: number;
  lotsTotal: number;
  commissionTotal: number;
  swapTotal: number;
  longs: SideStat;
  shorts: SideStat;
  bestTrade: number;
  worstTrade: number;
  bestTradePips: number;
  worstTradePips: number;
  avgTradeLengthMs: number;
  /** Infinity when there are wins but no losses (same as useTradeStatistics) */
  profitFactor: number;
  /** sample standard deviation of per-trade net P&L */
  stdDev: number;
  /** avg per-trade net P&L ÷ stdDev (per-trade Sharpe; not annualized) */
  sharpe: number | null;
  zScore: number | null;
  /** two-sided confidence (%) that the win/loss sequence is non-random */
  zProbabilityPct: number | null;
  /** expected net P&L per trade ($) */
  expectancy: number;
  expectancyPips: number;
  /** average holding-period return per trade (%) — balance-based */
  ahprPct: number | null;
  /** geometric holding-period return per trade (%) — balance-based */
  ghprPct: number | null;
}

export interface HeadlineStats {
  /** net trading profit / deposits × 100 (window-relative, not inception) */
  gainPct: number | null;
  /** compounded daily rate over the window */
  dailyPct: number | null;
  /** compounded monthly rate over the window */
  monthlyPct: number | null;
  /** max BALANCE drawdown (%) over the reconstructed curve */
  drawdownPct: number | null;
  endBalance: number;
  highestBalance: number;
  highestBalanceMs: number | null;
  netProfit: number;
  deposits: number;
  withdrawals: number;
}

export interface AccountAnalytics {
  tradeCount: number;
  firstTradeMs: number | null;
  lastTradeMs: number | null;
  stats: HeadlineStats;
  equity: EquityPoint[];
  dailyBuckets: DailyBucket[];
  trades: TradeStatistics;
  symbolSummary: SymbolSummaryGroup[];
  hourly: WinLossBucket[];
  weekday: WinLossBucket[];
  riskOfRuin: RiskOfRuinRow[];
  /** last N trades (close-time order), capped */
  durationScatter: DurationPoint[];
  monthly: MonthlyBucket[];
  years: number[];
}

export interface AccountAnalyticsInput {
  /** scoped positions (may include balance operations) */
  positions: TradeOrder[];
  /**
   * Balance operations for the same date window across ALL symbols. Balance
   * rows carry no symbol, so a symbol-scoped filter drops them — pass them
   * separately so deposits/gain% stay meaningful. Deduped by ticket.
   */
  balanceOperations?: TradeOrder[];
}

export const DURATION_SCATTER_CAP = 200;

// ─────────────────────────────────────────────────────────── helpers ──

const BUY_ORDER_TYPES = new Set<number>([
  VybeOrderType.Buy,
  VybeOrderType.BuyLimit,
  VybeOrderType.BuyStop,
  VybeOrderType.BuyStopLimit,
]);

const SELL_ORDER_TYPES = new Set<number>([
  VybeOrderType.Sell,
  VybeOrderType.SellLimit,
  VybeOrderType.SellStop,
  VybeOrderType.SellStopLimit,
]);

export function isBuyPositionType(orderType: number): boolean {
  return BUY_ORDER_TYPES.has(orderType);
}

export function isSellPositionType(orderType: number): boolean {
  return SELL_ORDER_TYPES.has(orderType);
}

/** Same convention as chart-helpers: pip = 10^-(digits-1), floor at 1. */
export function pipSizeForDigits(digits: number): number {
  return Math.pow(10, -Math.max((digits || 5) - 1, 0));
}

function parseTimeMs(iso: string | null | undefined): number {
  if (!iso || typeof iso !== "string") return 0;
  if (iso.startsWith("0001-01-01")) return 0;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t > 0 ? t : 0;
}

/** Position open time in ms (prefers openTimestampUTC, ms everywhere else). */
export function positionOpenMs(p: TradeOrder): number {
  if (typeof p.openTimestampUTC === "number" && p.openTimestampUTC > 0) {
    return p.openTimestampUTC;
  }
  return parseTimeMs(p.openTime);
}

/** Position close time in ms. */
export function positionCloseMs(p: TradeOrder): number {
  if (typeof p.closeTimestampUTC === "number" && p.closeTimestampUTC > 0) {
    return p.closeTimestampUTC;
  }
  return parseTimeMs(p.closeTime);
}

/** Signed pips of a closed trade (0 for non-directional rows). */
export function positionPips(p: TradeOrder): number {
  if (!p.openPrice || !p.closePrice) return 0;
  const pip = pipSizeForDigits(p.digits);
  if (isBuyPositionType(p.orderType)) return (p.closePrice - p.openPrice) / pip;
  if (isSellPositionType(p.orderType)) return (p.openPrice - p.closePrice) / pip;
  return 0;
}

export type StopHit = "sl" | "tp" | null;

/**
 * Labelled heuristic: did the trade close on its SL or TP?
 * Order of trust: out-deal placedType (authoritative when present) →
 * MT-style close comment markers → tolerance match of close price vs SL/TP.
 */
export function detectStopHit(p: TradeOrder): StopHit {
  const placed = p.dealInternalOut?.placedType;
  if (placed === VybePlacedType.OnSL || placed === VybePlacedType.OnStopOut) {
    return "sl";
  }
  if (placed === VybePlacedType.OnTP) return "tp";

  const comment = `${p.closeComment ?? ""} ${p.comment ?? ""}`.toLowerCase();
  if (comment.includes("[sl") || comment.includes("sl hit")) return "sl";
  if (comment.includes("[tp") || comment.includes("tp hit")) return "tp";

  if (!p.closePrice) return null;
  const point = Math.pow(10, -(p.digits || 5));
  const tolerance = Math.max(3 * point, Math.abs(p.closePrice) * 1e-4);
  if (p.stopLoss > 0 && Math.abs(p.closePrice - p.stopLoss) <= tolerance) {
    return "sl";
  }
  if (p.takeProfit > 0 && Math.abs(p.closePrice - p.takeProfit) <= tolerance) {
    return "tp";
  }
  return null;
}

/** "3d 4h" / "2h 13m" / "45m" / "30s" / "—" */
export function formatDurationShort(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function erf(x: number): number {
  // Abramowitz & Stegun 7.1.26
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * ax);
  const y =
    1 -
    (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t -
      0.284496736) *
      t +
      0.254829592) *
      t *
      Math.exp(-ax * ax);
  return sign * y;
}

function normalCdf(x: number): number {
  return 0.5 * (1 + erf(x / Math.SQRT2));
}

function localDayStart(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// ─────────────────────────────────────────────────────────── compute ──

interface LedgerEvent {
  t: number;
  delta: number;
  trade: TradeOrder | null; // null → balance operation
}

function emptySide(): SummarySide {
  return { trades: 0, pips: 0, profit: 0, wonPct: 0, lostPct: 0 };
}

function emptyStatistics(): TradeStatistics {
  return {
    totalTrades: 0,
    winCount: 0,
    lossCount: 0,
    breakEvenCount: 0,
    winRatePct: 0,
    totalProfit: 0,
    totalLoss: 0,
    netProfit: 0,
    pipsTotal: 0,
    avgWinPips: 0,
    avgLossPips: 0,
    avgWin: 0,
    avgLoss: 0,
    lotsTotal: 0,
    commissionTotal: 0,
    swapTotal: 0,
    longs: { total: 0, won: 0, winRatePct: 0 },
    shorts: { total: 0, won: 0, winRatePct: 0 },
    bestTrade: 0,
    worstTrade: 0,
    bestTradePips: 0,
    worstTradePips: 0,
    avgTradeLengthMs: 0,
    profitFactor: 0,
    stdDev: 0,
    sharpe: null,
    zScore: null,
    zProbabilityPct: null,
    expectancy: 0,
    expectancyPips: 0,
    ahprPct: null,
    ghprPct: null,
  };
}

export function computeAccountAnalytics(
  input: AccountAnalyticsInput
): AccountAnalytics {
  const { positions } = input;

  // Split scoped rows; merge in the all-symbol balance ops (dedupe by ticket).
  const trades: TradeOrder[] = [];
  const balanceByTicket = new Map<number, TradeOrder>();
  for (const p of positions) {
    if (isPositionBalanceOperation(p)) balanceByTicket.set(p.ticket, p);
    else trades.push(p);
  }
  for (const b of input.balanceOperations ?? []) {
    if (isPositionBalanceOperation(b)) balanceByTicket.set(b.ticket, b);
  }

  // Chronological event ledger (balance ops + trade closes).
  const events: LedgerEvent[] = [];
  for (const t of trades) {
    events.push({ t: positionCloseMs(t), delta: netPositionPnl(t), trade: t });
  }
  for (const b of balanceByTicket.values()) {
    const ts = positionCloseMs(b) || positionOpenMs(b);
    events.push({ t: ts, delta: b.profit ?? 0, trade: null });
  }
  events.sort((a, b) => a.t - b.t);

  let deposits = 0;
  let withdrawals = 0;
  let balance = 0;
  let cumProfit = 0;
  let peak = 0;
  let maxDrawdownPct: number | null = null;
  let highestBalance = 0;
  let highestBalanceMs: number | null = null;

  const equity: EquityPoint[] = [];
  const dailyMap = new Map<number, DailyBucket>();
  const monthlyMap = new Map<string, MonthlyBucket>();

  // Per-trade series (close-time order) for stdDev / runs test / AHPR.
  const tradePnls: number[] = [];
  const tradePips: number[] = [];
  const winLossSeq: boolean[] = [];
  const hprFactors: number[] = [];
  let hprUsable = 0;

  const stats = emptyStatistics();
  const hourly: WinLossBucket[] = Array.from({ length: 24 }, (_, key) => ({
    key,
    winners: 0,
    losers: 0,
  }));
  const weekday: WinLossBucket[] = Array.from({ length: 7 }, (_, key) => ({
    key,
    winners: 0,
    losers: 0,
  }));

  const symbolAgg = new Map<
    string,
    {
      long: { trades: number; pips: number; profit: number; won: number; lost: number };
      short: { trades: number; pips: number; profit: number; won: number; lost: number };
    }
  >();

  let firstTradeMs: number | null = null;
  let lastTradeMs: number | null = null;
  let totalTradeLengthMs = 0;
  let tradeLengthCount = 0;
  const durationAll: DurationPoint[] = [];

  for (const ev of events) {
    const balanceBefore = balance;
    balance += ev.delta;
    if (ev.trade) cumProfit += ev.delta;
    else if (ev.delta > 0) deposits += ev.delta;
    else if (ev.delta < 0) withdrawals += Math.abs(ev.delta);

    if (balance > peak) peak = balance;
    if (peak > 0) {
      const dd = ((peak - balance) / peak) * 100;
      if (maxDrawdownPct === null || dd > maxDrawdownPct) maxDrawdownPct = dd;
    }
    if (balance > highestBalance) {
      highestBalance = balance;
      highestBalanceMs = ev.t || null;
    }

    equity.push({
      t: ev.t,
      balance,
      profit: cumProfit,
      growthPct: null, // filled below once deposits are final
    });

    const trade = ev.trade;
    if (!trade) {
      // Balance ops shift month-open balances via the running `balance`.
      continue;
    }

    const closeMs = ev.t;
    const openMs = positionOpenMs(trade);
    const net = ev.delta;
    const pips = positionPips(trade);
    const isBuy = isBuyPositionType(trade.orderType);
    const isSell = isSellPositionType(trade.orderType);
    const win = net > 0;
    const loss = net < 0;

    if (closeMs > 0) {
      if (firstTradeMs === null || closeMs < firstTradeMs) firstTradeMs = closeMs;
      if (lastTradeMs === null || closeMs > lastTradeMs) lastTradeMs = closeMs;
    }

    // headline trade stats
    stats.totalTrades += 1;
    if (win) {
      stats.winCount += 1;
      stats.totalProfit += net;
      if (net > stats.bestTrade) stats.bestTrade = net;
    } else if (loss) {
      stats.lossCount += 1;
      stats.totalLoss += Math.abs(net);
      if (net < stats.worstTrade) stats.worstTrade = net;
    } else {
      stats.breakEvenCount += 1;
    }
    if (pips > stats.bestTradePips) stats.bestTradePips = pips;
    if (pips < stats.worstTradePips) stats.worstTradePips = pips;
    stats.pipsTotal += pips;
    stats.lotsTotal += trade.lots ?? 0;
    stats.commissionTotal += trade.commission ?? 0;
    stats.swapTotal += trade.swap ?? 0;
    if (isBuy) {
      stats.longs.total += 1;
      if (win) stats.longs.won += 1;
    } else if (isSell) {
      stats.shorts.total += 1;
      if (win) stats.shorts.won += 1;
    }

    tradePnls.push(net);
    tradePips.push(pips);
    if (win || loss) winLossSeq.push(win);

    if (balanceBefore > 0) {
      const factor = 1 + net / balanceBefore;
      hprFactors.push(factor);
      hprUsable += 1;
    }

    if (openMs > 0 && closeMs > openMs) {
      totalTradeLengthMs += closeMs - openMs;
      tradeLengthCount += 1;
    }
    durationAll.push({
      durationMs: openMs > 0 && closeMs > openMs ? closeMs - openMs : 0,
      pips,
      win,
    });

    // daily buckets (lots + trade pnl by close day)
    if (closeMs > 0) {
      const dayStart = localDayStart(closeMs);
      const bucket = dailyMap.get(dayStart) ?? { dayStart, lots: 0, pnl: 0 };
      bucket.lots += trade.lots ?? 0;
      bucket.pnl += net;
      dailyMap.set(dayStart, bucket);
    }

    // hour / weekday histograms by OPEN time (Myfxbook convention)
    const hd = new Date(openMs > 0 ? openMs : closeMs);
    if (win || loss) {
      const hb = hourly[hd.getHours()];
      const wb = weekday[hd.getDay()];
      if (win) {
        hb.winners += 1;
        wb.winners += 1;
      } else {
        hb.losers += 1;
        wb.losers += 1;
      }
    }

    // monthly buckets
    if (closeMs > 0) {
      const d = new Date(closeMs);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      let mb = monthlyMap.get(key);
      if (!mb) {
        mb = {
          year: d.getFullYear(),
          month: d.getMonth(),
          pnl: 0,
          // balance before this event = month-open balance the first time we
          // see the month (events are chronological)
          openBalance: balanceBefore,
          gainPct: null,
          trades: 0,
        };
        monthlyMap.set(key, mb);
      }
      mb.pnl += net;
      mb.trades += 1;
    }

    // per-symbol summary
    if (isBuy || isSell) {
      const sym = trade.symbol || "—";
      let agg = symbolAgg.get(sym);
      if (!agg) {
        agg = {
          long: { trades: 0, pips: 0, profit: 0, won: 0, lost: 0 },
          short: { trades: 0, pips: 0, profit: 0, won: 0, lost: 0 },
        };
        symbolAgg.set(sym, agg);
      }
      const side = isBuy ? agg.long : agg.short;
      side.trades += 1;
      side.pips += pips;
      side.profit += net;
      if (win) side.won += 1;
      else if (loss) side.lost += 1;
    }
  }

  // finalize growth% now deposits are known
  if (deposits > 0) {
    for (const pt of equity) {
      pt.growthPct = (pt.profit / deposits) * 100;
    }
  }

  // trade statistics derived values
  const decided = stats.winCount + stats.lossCount;
  stats.netProfit = stats.totalProfit - stats.totalLoss;
  stats.winRatePct = decided > 0 ? (stats.winCount / decided) * 100 : 0;
  stats.avgWin = stats.winCount > 0 ? stats.totalProfit / stats.winCount : 0;
  stats.avgLoss = stats.lossCount > 0 ? stats.totalLoss / stats.lossCount : 0;
  stats.profitFactor =
    stats.totalLoss > 0
      ? stats.totalProfit / stats.totalLoss
      : stats.totalProfit > 0
        ? Infinity
        : 0;
  stats.longs.winRatePct =
    stats.longs.total > 0 ? (stats.longs.won / stats.longs.total) * 100 : 0;
  stats.shorts.winRatePct =
    stats.shorts.total > 0 ? (stats.shorts.won / stats.shorts.total) * 100 : 0;
  stats.avgTradeLengthMs =
    tradeLengthCount > 0 ? totalTradeLengthMs / tradeLengthCount : 0;

  let winPips = 0;
  let winPipsCount = 0;
  let lossPips = 0;
  let lossPipsCount = 0;
  for (let i = 0; i < tradePnls.length; i++) {
    if (tradePnls[i] > 0) {
      winPips += tradePips[i];
      winPipsCount += 1;
    } else if (tradePnls[i] < 0) {
      lossPips += tradePips[i];
      lossPipsCount += 1;
    }
  }
  stats.avgWinPips = winPipsCount > 0 ? winPips / winPipsCount : 0;
  stats.avgLossPips = lossPipsCount > 0 ? lossPips / lossPipsCount : 0;

  const n = tradePnls.length;
  if (n > 0) {
    const mean = tradePnls.reduce((a, b) => a + b, 0) / n;
    stats.expectancy = mean;
    stats.expectancyPips = tradePips.reduce((a, b) => a + b, 0) / n;
    if (n > 1) {
      const variance =
        tradePnls.reduce((a, b) => a + (b - mean) * (b - mean), 0) / (n - 1);
      stats.stdDev = Math.sqrt(variance);
      stats.sharpe = stats.stdDev > 0 ? mean / stats.stdDev : null;
    }
  }

  // Z-score (runs test) over the win/loss sequence
  const N = winLossSeq.length;
  const W = winLossSeq.filter(Boolean).length;
  const L = N - W;
  if (N > 1 && W > 0 && L > 0) {
    let runs = 1;
    for (let i = 1; i < N; i++) {
      if (winLossSeq[i] !== winLossSeq[i - 1]) runs += 1;
    }
    const P = 2 * W * L;
    const denom = (P * (P - N)) / (N - 1);
    if (denom > 0) {
      const z = (N * (runs - 0.5) - P) / Math.sqrt(denom);
      stats.zScore = z;
      stats.zProbabilityPct = Math.min(
        100,
        Math.max(0, (2 * normalCdf(Math.abs(z)) - 1) * 100)
      );
    }
  }

  // AHPR / GHPR (balance-based; only usable when balance-before was positive)
  if (hprUsable > 0) {
    const ahpr = hprFactors.reduce((a, b) => a + b, 0) / hprFactors.length;
    stats.ahprPct = (ahpr - 1) * 100;
    if (hprFactors.every((f) => f > 0)) {
      const logSum = hprFactors.reduce((a, b) => a + Math.log(b), 0);
      stats.ghprPct = (Math.exp(logSum / hprFactors.length) - 1) * 100;
    }
  }

  // headline stats
  const netProfit = cumProfit;
  const gainPct = deposits > 0 ? (netProfit / deposits) * 100 : null;
  let dailyPct: number | null = null;
  let monthlyPct: number | null = null;
  if (
    gainPct !== null &&
    gainPct > -100 &&
    firstTradeMs !== null &&
    lastTradeMs !== null &&
    lastTradeMs > firstTradeMs
  ) {
    const days = (lastTradeMs - firstTradeMs) / 86_400_000;
    if (days >= 1) {
      const growthFactor = 1 + gainPct / 100;
      dailyPct = (Math.pow(growthFactor, 1 / days) - 1) * 100;
      const months = days / 30.44;
      monthlyPct =
        months >= 1
          ? (Math.pow(growthFactor, 1 / months) - 1) * 100
          : gainPct;
    } else {
      dailyPct = gainPct;
      monthlyPct = gainPct;
    }
  }

  const headline: HeadlineStats = {
    gainPct,
    dailyPct,
    monthlyPct,
    drawdownPct: maxDrawdownPct,
    endBalance: balance,
    highestBalance,
    highestBalanceMs,
    netProfit,
    deposits,
    withdrawals,
  };

  // risk of ruin table
  const riskBase = balance > 0 ? balance : deposits;
  const loseRate = decided > 0 ? stats.lossCount / decided : 0;
  const riskOfRuin: RiskOfRuinRow[] = [];
  for (let lossPct = 10; lossPct <= 100; lossPct += 10) {
    if (stats.avgLoss <= 0 || riskBase <= 0) {
      riskOfRuin.push({ lossPct, probabilityPct: null, consecutiveLosses: null });
      continue;
    }
    const consecutive = Math.max(
      1,
      Math.ceil(((lossPct / 100) * riskBase) / stats.avgLoss)
    );
    const probabilityPct =
      loseRate > 0 ? Math.pow(loseRate, consecutive) * 100 : 0;
    riskOfRuin.push({ lossPct, probabilityPct, consecutiveLosses: consecutive });
  }

  // per-symbol summary rows
  const symbolSummary: SymbolSummaryGroup[] = Array.from(symbolAgg.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([symbol, agg]) => {
      const toSide = (s: {
        trades: number;
        pips: number;
        profit: number;
        won: number;
        lost: number;
      }): SummarySide => ({
        trades: s.trades,
        pips: s.pips,
        profit: s.profit,
        wonPct: s.trades > 0 ? (s.won / s.trades) * 100 : 0,
        lostPct: s.trades > 0 ? (s.lost / s.trades) * 100 : 0,
      });
      const total = {
        trades: agg.long.trades + agg.short.trades,
        pips: agg.long.pips + agg.short.pips,
        profit: agg.long.profit + agg.short.profit,
        won: agg.long.won + agg.short.won,
        lost: agg.long.lost + agg.short.lost,
      };
      return {
        symbol,
        long: toSide(agg.long),
        short: toSide(agg.short),
        total: toSide(total),
      };
    });

  // monthly buckets + years
  const monthly = Array.from(monthlyMap.values()).sort(
    (a, b) => a.year - b.year || a.month - b.month
  );
  for (const mb of monthly) {
    mb.gainPct = mb.openBalance > 0 ? (mb.pnl / mb.openBalance) * 100 : null;
  }
  const years = Array.from(new Set(monthly.map((m) => m.year))).sort();

  return {
    tradeCount: stats.totalTrades,
    firstTradeMs,
    lastTradeMs,
    stats: headline,
    equity,
    dailyBuckets: Array.from(dailyMap.values()).sort(
      (a, b) => a.dayStart - b.dayStart
    ),
    trades: stats,
    symbolSummary,
    hourly,
    weekday,
    riskOfRuin,
    durationScatter: durationAll.slice(-DURATION_SCATTER_CAP),
    monthly,
    years,
  };
}

// ───────────────────────────────────────────────── monthly drilldown ──

export interface SymbolRewardRisk {
  symbol: string;
  /** avg win ÷ avg loss (net $); null when a side is missing */
  ratio: number | null;
  avgWin: number;
  avgLoss: number;
}

export interface SymbolHoldingTime {
  symbol: string;
  longMs: number | null;
  shortMs: number | null;
}

export interface MonthlyDrilldown {
  rewardRisk: SymbolRewardRisk[];
  holdingTimes: SymbolHoldingTime[];
}

/** Per-symbol Reward:Risk + average holding time for a given close month. */
export function computeMonthlyDrilldown(
  positions: TradeOrder[],
  year: number,
  month: number
): MonthlyDrilldown {
  interface Agg {
    winSum: number;
    winCount: number;
    lossSum: number;
    lossCount: number;
    longMsSum: number;
    longCount: number;
    shortMsSum: number;
    shortCount: number;
  }
  const bySymbol = new Map<string, Agg>();

  for (const p of positions) {
    if (isPositionBalanceOperation(p)) continue;
    const closeMs = positionCloseMs(p);
    if (closeMs <= 0) continue;
    const d = new Date(closeMs);
    if (d.getFullYear() !== year || d.getMonth() !== month) continue;

    const sym = p.symbol || "—";
    let agg = bySymbol.get(sym);
    if (!agg) {
      agg = {
        winSum: 0,
        winCount: 0,
        lossSum: 0,
        lossCount: 0,
        longMsSum: 0,
        longCount: 0,
        shortMsSum: 0,
        shortCount: 0,
      };
      bySymbol.set(sym, agg);
    }

    const net = netPositionPnl(p);
    if (net > 0) {
      agg.winSum += net;
      agg.winCount += 1;
    } else if (net < 0) {
      agg.lossSum += Math.abs(net);
      agg.lossCount += 1;
    }

    const openMs = positionOpenMs(p);
    const dur = openMs > 0 && closeMs > openMs ? closeMs - openMs : 0;
    if (dur > 0) {
      if (isBuyPositionType(p.orderType)) {
        agg.longMsSum += dur;
        agg.longCount += 1;
      } else if (isSellPositionType(p.orderType)) {
        agg.shortMsSum += dur;
        agg.shortCount += 1;
      }
    }
  }

  const symbols = Array.from(bySymbol.keys()).sort();
  return {
    rewardRisk: symbols.map((symbol) => {
      const a = bySymbol.get(symbol)!;
      const avgWin = a.winCount > 0 ? a.winSum / a.winCount : 0;
      const avgLoss = a.lossCount > 0 ? a.lossSum / a.lossCount : 0;
      return {
        symbol,
        ratio: avgWin > 0 && avgLoss > 0 ? avgWin / avgLoss : null,
        avgWin,
        avgLoss,
      };
    }),
    holdingTimes: symbols.map((symbol) => {
      const a = bySymbol.get(symbol)!;
      return {
        symbol,
        longMs: a.longCount > 0 ? a.longMsSum / a.longCount : null,
        shortMs: a.shortCount > 0 ? a.shortMsSum / a.shortCount : null,
      };
    }),
  };
}
